import { useMemo } from 'react';
import { 
  isCarouselItemActive, 
  getCarouselItemStatus, 
  getCarouselItemClass,
  filterActiveCarouselItems,
  sortCarouselItems,
  validateCarouselItem
} from '../utils/carouselUtils';

/**
 * Хук для роботи з елементами каруселі
 * @param {Array} items - масив елементів каруселі
 * @returns {Object} - об'єкт з утілітами та обробленими даними
 */
export const useCarouselItems = (items = []) => {
  // Мемоізуємо обчислення для оптимізації
  const processedItems = useMemo(() => {
    const activeItems = filterActiveCarouselItems(items);
    const sortedItems = sortCarouselItems(items);
    const sortedActiveItems = sortCarouselItems(activeItems);
    
    return {
      all: items,
      active: activeItems,
      sorted: sortedItems,
      sortedActive: sortedActiveItems,
      totalCount: items.length,
      activeCount: activeItems.length,
      inactiveCount: items.length - activeItems.length
    };
  }, [items]);

  // Статистика
  const stats = useMemo(() => {
    const itemsByType = items.reduce((acc, item) => {
      const type = item.slideType || 'unknown';
      if (!acc[type]) {
        acc[type] = { total: 0, active: 0, inactive: 0 };
      }
      acc[type].total++;
      if (isCarouselItemActive(item)) {
        acc[type].active++;
      } else {
        acc[type].inactive++;
      }
      return acc;
    }, {});

    return {
      byType: itemsByType,
      total: processedItems.totalCount,
      active: processedItems.activeCount,
      inactive: processedItems.inactiveCount,
      activePercentage: processedItems.totalCount > 0 
        ? Math.round((processedItems.activeCount / processedItems.totalCount) * 100) 
        : 0
    };
  }, [items, processedItems]);

  // Утіліти
  const utils = {
    isActive: isCarouselItemActive,
    getStatus: getCarouselItemStatus,
    getClass: getCarouselItemClass,
    validate: validateCarouselItem,
    filter: filterActiveCarouselItems,
    sort: sortCarouselItems
  };

  return {
    items: processedItems,
    stats,
    utils
  };
};

export default useCarouselItems;